import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Datetime from 'react-datetime';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import cn from 'classnames';
import { format } from 'date-fns';
import { get } from 'lodash';
import moment from 'moment';

import LoansAgreementModal from 'components/LoansAgreementModal';
import CancelPaymentModal from 'components/CancelPaymentModal';
import Validator from 'components/Validator/Validator';
import Loader from 'components/Loader';
import { ApplePayButton, Button, ButtonLink } from 'components/Button';
import Select from 'components/Form/Select';
import Input from 'components/Form/Input';
import { getCurrentDate } from 'utils/func';
import { formatCurrency } from 'utils/formatCurrency';
import { checkIfApplePayIsAvailable } from 'utils/helper';
import { currencyMask, floatUnmask, unmask } from 'utils/masks';
import { makeQuickpayPaymentAction, getQuickpayCollateralSummaryAction, getQuickpayLoanSetupAction } from 'actions/quickpay';
import {
  getCollateralSummaryHeldDisplay,
  isDtcCryptoLoan,
  toLtvPercentage,
} from 'containers/pages/Dashboard/utils/cryptoUtils';
import LtvSummaryCard from 'containers/pages/Dashboard/components/LowerLtvModal/LtvSummaryCard';
import 'containers/pages/Dashboard/components/LowerLtvModal/style.scss';
import { getQuickpayLoanSetupThresholds } from './cryptoUtils';
import { shouldRedirectToLowerLtv } from './quickpayLandingRedirect';

import styles from './styles.module.scss';
import makePaymentSchema from './schemas/makePaymentSchema';
import applePaySchema from './schemas/applePaySchema';

const DEFAULT_MARGIN_CALL_THRESHOLD = 75;
const DEFAULT_LIQUIDATION_THRESHOLD = 90;

const SimplePay = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const quickpayData = useSelector((state) => state.quickpay.data);
  const collateralSummaryState = useSelector((state) => state.quickpay.collateralSummary);
  const loanSetupState = useSelector((state) => state.quickpay.loanSetup);
  const collateralSummary = collateralSummaryState?.data;
  const isSummaryLoading = collateralSummaryState?.isLoading;
  const isCryptoLoan = isDtcCryptoLoan(quickpayData);
  const { loanId } = quickpayData;
  const suggestAutopay = (quickpayData.autopays || []).length === 0 && quickpayData.suggestedAutopayNextApplyDate && quickpayData.suggestedAutopayAmount;
  const [paymentModal, setPaymentModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const goToScheduleAutopay = () => navigate('/quickpay/enroll-autopay');

  const daysPastDue = Number(get(quickpayData, 'daysPastDue', 0)) || 0;
  const isPastDue = daysPastDue > 0;
  const hasAmountPastDue = useMemo(
    () => isPastDue && quickpayData.amountPastDue > 0,
    [isPastDue, quickpayData.amountPastDue]
  );
  const headerPaymentAmount = hasAmountPastDue
    ? (quickpayData.amountPastDue + quickpayData.dueFees)
    : quickpayData.totalDue;
  const titleStr = useMemo(
    () => (!hasAmountPastDue && !quickpayData.nextPaymentDate && quickpayData.dueFees === 0
      ? 'Make an additional payment'
      : 'Make a one-time payment'),
    [quickpayData, hasAmountPastDue]
  );
  const headerStr = useMemo(() => {
    if (hasAmountPastDue) {
      return 'You have a past due payment of';
    }

    if (quickpayData.nextPaymentDate) {
      return `You have a payment due on ${moment(quickpayData.nextPaymentDate).format('MM/DD/YYYY')} for`;
    }

    if (quickpayData.dueFees > 0) {
      return 'You have a payment due today for';
    }

    return 'Great job, you are all caught up!';
  }, [quickpayData]);
  const isApplePayAvailable = checkIfApplePayIsAvailable();

  const {
    validator: {
      values,
      setValues,
      onChangeHandler,
      validate,
      errors,
    },
  } = props;

  useEffect(() => {
    const primaryPaymentMethod = (quickpayData.paymentAccounts || []).find((acc) => acc.isPrimary);
    setValues({
      paymentAccountId: primaryPaymentMethod?.accountId,
      applyDate: getCurrentDate(),
      amount: hasAmountPastDue ? (quickpayData.amountPastDue + quickpayData.dueFees) : quickpayData.totalDue,
      selectedAmountOption: hasAmountPastDue ? '1' : '0',
      rawData: quickpayData,
    });
    if (!primaryPaymentMethod) setIsEdit(true);
  }, []);

  useEffect(() => {
    if (!loanId || !isCryptoLoan) return;

    dispatch(getQuickpayCollateralSummaryAction({
      loanId,
    }));
    dispatch(getQuickpayLoanSetupAction({
      loanId,
    }));
  }, [dispatch, loanId, isCryptoLoan]);

  const currentLtv = collateralSummary?.currentLtv;
  const {
    marginCallPct: apiMarginCallPct,
    liquidationPct: apiLiquidationPct,
    contractualLtvPct: apiContractualLtvPct,
  } = getQuickpayLoanSetupThresholds(loanSetupState?.data);
  const marginCallThreshold = apiMarginCallPct != null
    ? Math.round(apiMarginCallPct)
    : DEFAULT_MARGIN_CALL_THRESHOLD;
  const liquidationThreshold = apiLiquidationPct != null
    ? Math.round(apiLiquidationPct)
    : DEFAULT_LIQUIDATION_THRESHOLD;
  const contractualLtvPct = apiContractualLtvPct != null ? Math.round(apiContractualLtvPct) : null;
  const currentLtvPct = toLtvPercentage(currentLtv);
  const isPastDueAndLtvAboveContract = isPastDue
    && currentLtvPct != null
    && contractualLtvPct != null
    && currentLtvPct > contractualLtvPct;
  const paymentButtonLabel = isPastDueAndLtvAboveContract ? 'Pay Past Due' : 'Make Payment';
  const showPastDueCryptoBanners = isCryptoLoan && isPastDue && !isSummaryLoading;
  const showLtvBanner = isCryptoLoan && !isSummaryLoading && !showPastDueCryptoBanners;
  const collateralHeldDisplay = useMemo(
    () => getCollateralSummaryHeldDisplay(collateralSummary).collateralHeld || '-',
    [collateralSummary]
  );

  const renderCollateralBanner = () => (
    <div className="lower-ltv-modal lower-ltv-modal--embedded">
      <LtvSummaryCard
        collateralHeld={collateralHeldDisplay}
        currentLtv={currentLtv}
        contractualLtvPct={contractualLtvPct}
        marginCallThreshold={marginCallThreshold}
        liquidationThreshold={liquidationThreshold}
        showContractualWarning={false}
      />
    </div>
  );

  const allPaymentMethods = (quickpayData?.paymentAccounts || []).map((acc) => ({
    value: acc.accountId,
    title: `${(acc.accountType)} ${acc.category === 'bankcard' ? `xxxx-xxxx-xxxx-${acc.cardNumberLast4}` : `xxxx${acc.accountNumberLast4}`}`,
  }));

  allPaymentMethods.push({
    value: 'addPaymentMethod',
    title: '+ Add Payment Method ...',
  });

  const paymentAmounts = [
    {
      title: `Total Due - $${quickpayData.totalDue}`,
      value: '0',
    },
    {
      title: `Past Due (including fees) - $${formatCurrency(quickpayData.amountPastDue + quickpayData.dueFees, 2)}`,
      value: '1',
    },
    {
      title: 'Other Amount',
      value: '2',
    },
  ];

  const toggleModal = (type) => {
    if (type === 'cancel') {
      setCancelModal(!cancelModal);
      setPaymentModal(false);
    } else {
      setPaymentModal(!paymentModal);
      setCancelModal(false);
    }
  };

  const getAmountToPay = (isApplePay = false) => {
    let nextAmount = 0;
    switch (values.selectedAmountOption) {
      case '0':
        nextAmount = isApplePay ? quickpayData.totalDue : (quickpayData.totalDue - quickpayData.dueFees);
        break;
      case '1':
        nextAmount = quickpayData.amountPastDue + quickpayData.dueFees;
        break;
      case '2':
        nextAmount = Number(unmask(values.customPaymentAmount));
        break;
      default:
        break;
    }
    return nextAmount;
  };

  const toggleEdit = () => {
    setIsEdit(!isEdit);
  };

  const renderFromAccount = () => {
    const selectedMethod = allPaymentMethods.find((acc) => acc.value === values.paymentAccountId);
    return selectedMethod?.title;
  };

  const handleInputChange = (event) => {
    switch (event.target.name) {
      case 'payFromAccount':
      case 'paymentAccountId':
        if (event.target.value === 'addPaymentMethod') {
          navigate('/quickpay/add-payment-method');
        } else {
          onChangeHandler(event.target.name, event.target.value);
        }
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  const handleBlur = (event) => {
    onChangeHandler(event.target.name, formatCurrency(floatUnmask(event.target.value || '0'), 2));
  };

  const handleDateChange = (date) => {
    if (date && typeof date.toISOString === 'function') {
      onChangeHandler('applyDate', format(new Date(date.toISOString()), 'MM/dd/yyyy'));
    } else {
      onChangeHandler('applyDate', date);
    }
  };

  const validateDate = (current) => {
    if (get(quickpayData, 'daysPastDue', 0) > 45) {
      return current.isAfter(moment().subtract(1, 'day')) && current.isBefore(moment());
    }
    return current.isAfter(moment().subtract(1, 'day')) && current.isBefore(moment().add(45 - get(quickpayData, 'daysPastDue', 0), 'days'));
  };

  const validatePaymentRequest = (schema) => validate(schema).isValid;

  const handlePostPayment = (applePayData, successCallback, failCallback) => {
    setErrorMsg('');
    const schema = applePayData ? applePaySchema : makePaymentSchema;

    if (validatePaymentRequest(schema)) {
      setIsLoading(true);
      const selectedAccount = quickpayData.paymentAccounts.find((acc) => acc.accountId === values.paymentAccountId);
      const paymentMethodCode = applePayData ? 'apple-pay' : selectedAccount.category === 'bankcard' ? 'debit-card' : 'bank-account';
      let amountToPay = getAmountToPay();
      let fees = quickpayData.dueFees;

      if (values.selectedAmountOption === '1') {
        amountToPay = quickpayData.amountPastDue;
      } else if (values.selectedAmountOption === '2') {
        fees = 0;
      }

      dispatch(makeQuickpayPaymentAction({
        data: {
          paymentAccountId: !applePayData ? values.paymentAccountId : undefined,
          paymentMethodCode,
          applyDate: moment(values.applyDate).format('YYYY-MM-DD'),
          amount: amountToPay,
          fees,
          isPayoff: false,
          applePay: applePayData || undefined,
        },
        success: (res) => {
          setIsLoading(false);
          if (res && res.some((item) => get(item, 'paymentProcessor.status') === 'Failed')) {
            setErrorMsg('We encountered an error while processing payment.');
            failCallback && failCallback();
          } else if (shouldRedirectToLowerLtv({ ...quickpayData, daysPastDue: 0 }, collateralSummary, loanSetupState?.data)) {
            navigate('/quickpay/lower-your-ltv', {
              replace: true,
              state: { paymentConfirmedAmount: getAmountToPay() + quickpayData.dueFees },
            });
            successCallback && successCallback();
          } else {
            navigate('/quickpay/payment-submitted');
            successCallback && successCallback();
          }
        },
        fail: (err) => {
          setErrorMsg(get(err, 'data.message') || get(err, 'data.errorMessages.0') || 'An error occured.');
          setIsLoading(false);
          failCallback && failCallback();
        },
      }));
    } else if (!isEdit) {
      toggleEdit();
    }
  };

  return (
    <div className={cn(styles.quickpay, styles.simplepay)}>
      <div className={cn(styles.content, 'text-center')}>
        <h2>
          Hey
          {' '}
          {quickpayData.firstName}
          !
        </h2>
        {isCryptoLoan && isPastDue && isSummaryLoading ? (
          <Loader className="height-2 mb-2" />
        ) : showPastDueCryptoBanners ? (
          <>
            <p className={cn('mb-0', styles.error)}>Your payment is past due</p>
            <div className={styles.payPastDueStatusStack}>
              <div className={styles.cryptoPastDueBox}>
                <span className={styles.cryptoLabel}>Total DUE</span>
                <div className={styles.cryptoPastDueBoxRow}>
                  <div className={cn(styles.cryptoValue, styles.error)}>
                    $
                    {formatCurrency(headerPaymentAmount, 2)}
                  </div>
                  {daysPastDue > 0 ? (
                    <span className={styles.cryptoPastDueOverdue}>
                      {daysPastDue}
                      {' '}
                      days overdue
                    </span>
                  ) : null}
                </div>
              </div>
              {renderCollateralBanner()}
            </div>
          </>
        ) : (
          <>
            {showLtvBanner && renderCollateralBanner()}
            {!showLtvBanner && (
              <>
                <p className="mb-0">{headerStr}</p>
                <span className={`${styles.paymentAmount} ${hasAmountPastDue ? styles.error : ''}`}>
                  $
                  {formatCurrency(headerPaymentAmount, 2)}
                </span>
              </>
            )}
          </>
        )}
        <div className={styles.paymentInfo}>
          <h1>{titleStr}</h1>
          {isEdit ? (
            <div>
              <Select
                className="mb-2"
                name="paymentAccountId"
                data={allPaymentMethods}
                value={values.paymentAccountId}
                onChange={handleInputChange}
                label="Pay From Account"
                isRequired
                hasError={!!errors.paymentAccountId}
                errorMessage={errors.paymentAccountId}
              />
              <div className="modal-dialog">
                <div className={cn('has-value', 'hasValue', 'w-100', 'mb-1', errors?.applyDate ? 'required' : '')}>
                  <span>
                    Payment Date
                    <em>Required</em>
                  </span>
                  <Datetime
                    name="applyDate"
                    label="Payment Date"
                    value={values.applyDate}
                    onChange={handleDateChange}
                    closeOnSelect
                    dateFormat="MM/DD/YYYY"
                    timeFormat={false}
                    placeHolder="MM/DD/YYYY"
                    isValidDate={validateDate}
                    renderInput={(propsInput) => (
                      <Input
                        isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                        isRequired
                        hasError={!!errors.applyDate}
                        errorMessage={errors.applyDate}
                        {...propsInput}
                      />
                    )}
                  />
                </div>
              </div>
              <Select
                className="mb-2"
                name="selectedAmountOption"
                label="Payment Amount"
                data={paymentAmounts}
                value={values.selectedAmountOption}
                onChange={handleInputChange}
                isRequired
                hasError={!!errors.selectedAmountOption}
                errorMessage={errors.selectedAmountOption}
              />
              {values.selectedAmountOption === '2' ? (
                <Input
                  label="Custom Payment Amount"
                  name="customPaymentAmount"
                  inputMode="decimal"
                  value={values.customPaymentAmount}
                  onChange={handleInputChange}
                  hasError={!!errors.customPaymentAmount}
                  errorMessage={errors.customPaymentAmount}
                  onBlur={handleBlur}
                  isMasked={currencyMask}
                  isRequired
                />
              ) : null}
            </div>
          ) : (
            <table>
              <tbody>
                <tr>
                  <td>From Account</td>
                  <td className={styles.paymentCell}>{renderFromAccount()}</td>
                </tr>
                <tr>
                  <td>Date</td>
                  <td>{values.applyDate}</td>
                </tr>
                <tr>
                  <td>Amount</td>
                  <td>
                    $
                    {formatCurrency(values.amount, 2)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {!isEdit && <span className={styles.anchorBtn} onClick={toggleEdit}>EDIT</span>}
        </div>
      </div>
      <div className={styles.stickyFooter}>
        {errorMsg ? (
          <div className={cn(styles.error, 'mb-1')}>
            {errorMsg}
          </div>
        ) : null}
        <small className="text-center d-block mb-1">
          By clicking
          {isApplePayAvailable ? ' "Apple Pay" or ' : ' '}
          &#34;Make Payment&#34;, I authorize LendingUSA to make a one-time debit and/or charge from my account as designated above.
        </small>
        <div className="mt-1 mb-2 w-100">
          <Button
            color={isPastDueAndLtvAboveContract ? 'danger' : 'primary'}
            className="w-100"
            onClick={() => handlePostPayment()}
            isLoading={isLoading}
            isDisabled={getAmountToPay() === 0}
          >
            {paymentButtonLabel}
          </Button>
          {isApplePayAvailable && (
            <ApplePayButton
              className="w-100"
              getAmountToPay={() => getAmountToPay(true)}
              processPayment={handlePostPayment}
              validatePayment={() => validatePaymentRequest(applePaySchema)}
              disabled={getAmountToPay() === 0}
            />
          )}
        </div>
        {suggestAutopay ? (
          <Button
            color="primary"
            className="w-100 mt-1 mb-2"
            onClick={goToScheduleAutopay}
            isLoading={isLoading}
          >
            Setup Autopay
          </Button>
        ) : null}
        <div style={{ textAlign: 'center', marginTop: 5 }}>
          <ButtonLink
            className="d-block text-center btn-link"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleModal('payment')}
          >
            How will my payment be applied?
          </ButtonLink>
        </div>
        <div style={{ textAlign: 'center', marginTop: 5 }}>
          <ButtonLink
            className="d-block text-center btn-link"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleModal('cancel')}
          >
            How can I cancel my payment?
          </ButtonLink>
        </div>
      </div>
      <LoansAgreementModal
        className={styles.modal}
        isOpen={paymentModal}
        toggle={() => toggleModal('payment')}
        paddingTop={1}
        showCloseBtn
        footerLink={<div style={{ marginTop: 16 }} />}
      />
      <CancelPaymentModal
        className={styles.modal}
        isOpen={cancelModal}
        toggle={() => toggleModal('cancel')}
        paddingTop={1}
        showCloseBtn
        footerLink={<div style={{ marginTop: 16 }} />}
      />
    </div>
  );
};

SimplePay.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(Validator(makePaymentSchema))(SimplePay);
